import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, BarRow, Grid, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n)    { return n === null || n === undefined ? '\u2026' : n.toLocaleString(); }
function pct(a, b) { return b ? Math.round((a / b) * 100) : 0; }

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function CoverageBar({ label, value, pct: p, sublabel, color }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 5 }}>
        <div>
          <span style={{ fontSize: '.73rem', color: '#1a3040', fontWeight: 500 }}>{label}</span>
          {sublabel && <span style={{ fontSize: '.6rem', color: '#9eb8c8', marginLeft: 6 }}>{sublabel}</span>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: '.73rem', fontWeight: 600, color: '#0f1e28' }}>{fmt(value)}</span>
          <span style={{ fontSize: '.63rem', color: '#9eb8c8', marginLeft: 4 }}>({p}%)</span>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#eef2f5', overflow: 'hidden' }}>
        <div style={{ width: p + '%', height: '100%', borderRadius: 3, transition: 'width .8s ease',
          background: color || (p > 70 ? '#2da06a' : p > 40 ? '#d97706' : '#e05c5c') }} />
      </div>
    </div>
  );
}

export default function Coverage({ onNav }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [
          { data: cov },
          { data: qual },
          { data: ungen },
          { count: gpsCoords },
          { count: totalLocs },
        ] = await Promise.all([
          supabase.rpc('get_coverage_stats'),
          supabase.rpc('get_quality_by_continent'),
          supabase.rpc('get_ungeocoded_by_country'),
          supabase.from('Locations').select('*', { count: 'exact', head: true })
            .not('latitude', 'is', null).neq('latitude', 0),
          supabase.from('Locations').select('*', { count: 'exact', head: true }),
        ]);
        if (!cancelled) setData({
          cov, qual: qual || [], ungen: ungen || [],
          gpsCoords: gpsCoords || 0, totalLocs: totalLocs || 0,
        });
      } catch (_e) { console.error('Coverage error:', _e); }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const cov    = data?.cov    || {};
  const qual   = data?.qual   || [];
  const ungen  = data?.ungen  || [];

  const total       = data?.totalLocs || cov.total_locations || cov.totalLocations || 0;
  const gpsCoords   = data?.gpsCoords || 0;
  const gpsRate     = pct(gpsCoords, total);
  const byContinent = cov.byContinent || [];
  const maxC        = byContinent.reduce((m, r) => Math.max(m, r.count || 0), 1);

  const topUngeocoded = (ungen || []).slice(0, 8);
  const maxUn = topUngeocoded[0]?.ungeocoded ?? topUngeocoded[0]?.count ?? 1;

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <Grid cols={4}>
        <MetricCard
          label="Total locations"
          value={loading ? '\u2026' : fmt(total)}
          sub={!loading ? (cov.totalCountries || cov.countryCount || 0) + ' countries' : undefined}
          accent="#314F5C"
        />
        <MetricCard
          label="GPS coverage"
          value={loading ? '\u2026' : gpsRate + '%'}
          sub={!loading ? fmt(gpsCoords) + ' with coordinates' : undefined}
          trend={gpsRate > 80 ? 'up' : 'down'}
          accent={gpsRate > 80 ? '#2da06a' : '#d97706'}
        />
        <MetricCard
          label="Missing GPS"
          value={loading ? '\u2026' : fmt(total - gpsCoords)}
          sub={!loading ? (100 - gpsRate) + '% of database' : undefined}
          trend={(total - gpsCoords) > total * 0.1 ? 'down' : null}
          accent={(total - gpsCoords) > total * 0.1 ? '#e05c5c' : '#9eb8c8'}
        />
        <MetricCard
          label="Continents"
          value={loading ? '\u2026' : fmt(byContinent.length || cov.totalContinents || 0)}
          sub="in the database"
          accent="#6F8FA7"
        />
      </Grid>

      <Grid cols={2} mb={0}>

        <Card style={{ marginBottom: 0 }}>
          <CardTitle>Coverage by continent</CardTitle>
          {loading ? [1,2,3,4].map(i => <Skel key={i} h={44} mb={14} />) : (
            byContinent.length === 0
              ? <Insight type="info">No continent data available.</Insight>
              : byContinent.map(row => (
                  <CoverageBar
                    key={row.continent}
                    label={row.continent || 'Unknown'}
                    value={row.count}
                    pct={pct(row.count, maxC)}
                    color="#314F5C"
                  />
                ))
          )}
        </Card>

        <div>
          {qual.length > 0 && (
            <Card>
              <CardTitle>Quality score by continent</CardTitle>
              {qual.slice(0, 5).map(row => {
                const score = row.total ? Math.round((row.has_coords || 0) / row.total * 100) : 0;
                return (
                  <BarRow
                    key={row.continent}
                    label={row.continent || 'Unknown'}
                    value={score + '%'}
                    pct={score}
                    color={score > 70 ? '#2da06a' : score > 40 ? '#d97706' : '#e05c5c'}
                    labelWidth={110}
                  />
                );
              })}
            </Card>
          )}

          <Card style={{ marginBottom: 0 }}>
            <CardTitle>Most ungeocoded countries</CardTitle>
            {loading ? [1,2,3,4,5].map(i => <Skel key={i} mb={8} />) : (
              topUngeocoded.length === 0
                ? <Insight type="ok">All locations have GPS coordinates.</Insight>
                : topUngeocoded.map(row => (
                    <BarRow
                      key={row.country}
                      label={row.country || 'Unknown'}
                      value={fmt(row.ungeocoded ?? row.count ?? 0)}
                      pct={pct(row.ungeocoded ?? row.count ?? 0, maxUn)}
                      color="#e05c5c"
                      labelWidth={120}
                    />
                  ))
            )}
          </Card>
        </div>
      </Grid>
    </div>
  );
}
